import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

/**
 * Capture chart as image with white background
 * @param {HTMLCanvasElement} chartCanvas - The chart canvas element
 * @returns {Promise<string>} - Base64 image data
 */
const captureChartImage = async (chartCanvas) => {
  try {
    if (!chartCanvas) return null;

    // Create a temporary canvas with white background
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = chartCanvas.width;
    tempCanvas.height = chartCanvas.height;
    const tempCtx = tempCanvas.getContext('2d');

    // Fill with white background
    tempCtx.fillStyle = '#FFFFFF';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Draw the original chart on top
    tempCtx.drawImage(chartCanvas, 0, 0);

    // Get the canvas as a data URL (base64 encoded PNG)
    const imageData = tempCanvas.toDataURL('image/png');
    return imageData;
  } catch (error) {
    console.error('Error capturing chart image:', error);
    return null;
  }
};

/**
 * Export graph data to XLSX format with chart image
 * Includes formatted report with charts, summary statistics, and organizational branding
 *
 * @param {Object} data - The data object containing labels and values
 * @param {string} filename - The name for the exported file
 * @param {Object} metadata - Additional metadata (title, subtitle, filters, etc.)
 * @param {HTMLCanvasElement} chartCanvas - Optional canvas element to include chart image
 */
export const exportToXLSX = async (data, filename, metadata = {}, chartCanvas = null) => {
  try {
    // If chart canvas is provided, use ExcelJS for image support
    if (chartCanvas) {
      const workbook = new ExcelJS.Workbook();

      // Create Summary worksheet
      const summarySheet = workbook.addWorksheet('Summary');

      let currentCol = 1;

      // Row 1: Headers (Bold) with column width calculation
      if (metadata.title) {
        summarySheet.getRow(1).getCell(currentCol).value = 'Graph Name';
        summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
        summarySheet.getRow(1).getCell(currentCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        const width = Math.max('Graph Name'.length, (metadata.title || '').length) + 10;
        summarySheet.getColumn(currentCol).width = width;
        currentCol++;
      }

      if (metadata.selectedDataset) {
        summarySheet.getRow(1).getCell(currentCol).value = 'Selected Dataset';
        summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
        summarySheet.getRow(1).getCell(currentCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        const width = Math.max('Selected Dataset'.length, (metadata.selectedDataset || '').length) + 10;
        summarySheet.getColumn(currentCol).width = width;
        currentCol++;
      }

      if (metadata.selectedGroup) {
        summarySheet.getRow(1).getCell(currentCol).value = 'Selected Group';
        summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
        summarySheet.getRow(1).getCell(currentCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        const width = Math.max('Selected Group'.length, (metadata.selectedGroup || '').length) + 10;
        summarySheet.getColumn(currentCol).width = width;
        currentCol++;
      }

      if (metadata.selectedUsers) {
        summarySheet.getRow(1).getCell(currentCol).value = 'Selected Users';
        summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
        summarySheet.getRow(1).getCell(currentCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        const width = Math.max('Selected Users'.length, (metadata.selectedUsers || '').length) + 10;
        summarySheet.getColumn(currentCol).width = width;
        currentCol++;
      }

      if (metadata.dateRange) {
        summarySheet.getRow(1).getCell(currentCol).value = 'Date Range';
        summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
        summarySheet.getRow(1).getCell(currentCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        const width = Math.max('Date Range'.length, (metadata.dateRange || '').length) + 10;
        summarySheet.getColumn(currentCol).width = width;
        currentCol++;
      }

      if (metadata.subtitle) {
        summarySheet.getRow(1).getCell(currentCol).value = 'Description';
        summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
        summarySheet.getRow(1).getCell(currentCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        const width = Math.max('Description'.length, (metadata.subtitle || '').length) + 10;
        summarySheet.getColumn(currentCol).width = width;
        currentCol++;
      }

      summarySheet.getRow(1).getCell(currentCol).value = 'Export Date';
      summarySheet.getRow(1).getCell(currentCol).font = { bold: true };
      summarySheet.getRow(1).getCell(currentCol).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E7E6E6' }
      };
      const exportDateWidth = Math.max('Export Date'.length, new Date().toLocaleString().length) + 10;
      summarySheet.getColumn(currentCol).width = exportDateWidth;

      // Row 2: Values
      currentCol = 1;

      if (metadata.title) {
        summarySheet.getRow(2).getCell(currentCol).value = metadata.title;
        currentCol++;
      }

      if (metadata.selectedDataset) {
        summarySheet.getRow(2).getCell(currentCol).value = metadata.selectedDataset;
        currentCol++;
      }

      if (metadata.selectedGroup) {
        summarySheet.getRow(2).getCell(currentCol).value = metadata.selectedGroup;
        currentCol++;
      }

      if (metadata.selectedUsers) {
        summarySheet.getRow(2).getCell(currentCol).value = metadata.selectedUsers;
        currentCol++;
      }

      if (metadata.dateRange) {
        summarySheet.getRow(2).getCell(currentCol).value = metadata.dateRange;
        currentCol++;
      }

      if (metadata.subtitle) {
        summarySheet.getRow(2).getCell(currentCol).value = metadata.subtitle;
        currentCol++;
      }

      summarySheet.getRow(2).getCell(currentCol).value = new Date().toLocaleString();

      let currentRow = 5; // Start from row 5 (increased gap from headers)

      // Add data table on the left side (starting from column A)
      const dataStartCol = 1; // Column A (ExcelJS uses 1-based indexing: A=1)
      let dataCurrentRow = currentRow;

      if (data.labels && data.values) {
        // Calculate column widths based on content
        const labelHeader = metadata.labelHeader || 'Label';
        const valueHeader = metadata.valueHeader || 'Value';

        // Find max length in labels
        const maxLabelLength = Math.max(
          labelHeader.length,
          ...data.labels.map(label => String(label).length)
        );

        // Find max length in values
        const maxValueLength = Math.max(
          valueHeader.length,
          ...data.values.map(value => String(value).length)
        );

        // Set column widths with padding
        summarySheet.getColumn(dataStartCol).width = maxLabelLength + 10;
        summarySheet.getColumn(dataStartCol + 1).width = maxValueLength + 10;

        // Add data section header
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Graph Data';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).font = { bold: true, size: 12 };
        dataCurrentRow++;

        // Add column headers
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = metadata.labelHeader || 'Label';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).value = metadata.valueHeader || 'Value';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).font = { bold: true };
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).font = { bold: true };
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'E7E6E6' }
        };
        dataCurrentRow++;

        // Add data rows
        data.labels.forEach((label, index) => {
          summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = label;
          summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).value = data.values[index] || 0;
          dataCurrentRow++;
        });

        // Add summary statistics
        dataCurrentRow++;
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Summary Statistics';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).font = { bold: true };
        dataCurrentRow++;

        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Total';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).value = data.values.reduce((sum, val) => sum + val, 0);
        dataCurrentRow++;

        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Average';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).value = parseFloat((data.values.reduce((sum, val) => sum + val, 0) / data.values.length).toFixed(2));
        dataCurrentRow++;

        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Maximum';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).value = Math.max(...data.values);
        dataCurrentRow++;

        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Minimum';
        summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + 1).value = Math.min(...data.values);
      } else if (Array.isArray(data)) {
        // If data is an array of objects
        if (data.length > 0) {
          const headers = Object.keys(data[0]);

          // Calculate column widths based on content
          headers.forEach((header, index) => {
            // Find max length for this column (considering header and all values)
            const maxLength = Math.max(
              header.length,
              ...data.map(row => String(row[header] || '').length)
            );
            summarySheet.getColumn(dataStartCol + index).width = maxLength + 10;
          });

          // Add data section header
          summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).value = 'Graph Data';
          summarySheet.getRow(dataCurrentRow).getCell(dataStartCol).font = { bold: true, size: 12 };
          dataCurrentRow++;

          // Add column headers
          headers.forEach((header, index) => {
            summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + index).value = header;
            summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + index).font = { bold: true };
            summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + index).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'E7E6E6' }
            };
          });
          dataCurrentRow++;

          // Add data rows
          data.forEach(row => {
            headers.forEach((header, index) => {
              summarySheet.getRow(dataCurrentRow).getCell(dataStartCol + index).value = row[header];
            });
            dataCurrentRow++;
          });
        }
      }

      // Add chart image on the right side (starting from column D or E depending on data table width)
      const imageData = await captureChartImage(chartCanvas);
      if (imageData) {
        const imageId = workbook.addImage({
          base64: imageData,
          extension: 'png',
        });

        // Position image to the right of data table (column D = index 3, but 0-based for images)
        summarySheet.addImage(imageId, {
          tl: { col: 3, row: currentRow - 1 },
          ext: { width: 600, height: 300 }
        });
      }

      // Write to file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.xlsx`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true;
    } else {
      // Fallback to basic XLSX without images
      const workbook = XLSX.utils.book_new();

      const summaryData = [];
      if (metadata.title) summaryData.push(['Report Title', metadata.title]);
      if (metadata.subtitle) summaryData.push(['Description', metadata.subtitle]);
      if (metadata.totalCount !== undefined) summaryData.push(['Total Count', metadata.totalCount]);
      if (metadata.dateRange) summaryData.push(['Date Range', metadata.dateRange]);
      if (metadata.filters) summaryData.push(['Filters Applied', metadata.filters]);
      summaryData.push([]);
      summaryData.push(['Export Date', new Date().toLocaleString()]);
      summaryData.push([]);

      const detailedData = [];
      if (data.labels && data.values) {
        detailedData.push([metadata.labelHeader || 'Label', metadata.valueHeader || 'Value']);
        data.labels.forEach((label, index) => {
          detailedData.push([label, data.values[index] || 0]);
        });
        detailedData.push([]);
        detailedData.push(['Summary Statistics']);
        detailedData.push(['Total', data.values.reduce((sum, val) => sum + val, 0)]);
        detailedData.push(['Average', (data.values.reduce((sum, val) => sum + val, 0) / data.values.length).toFixed(2)]);
        detailedData.push(['Maximum', Math.max(...data.values)]);
        detailedData.push(['Minimum', Math.min(...data.values)]);
      } else if (Array.isArray(data)) {
        if (data.length > 0) {
          const headers = Object.keys(data[0]);
          detailedData.push(headers);
          data.forEach(row => {
            detailedData.push(headers.map(header => row[header]));
          });
        }
      }

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      const dataSheet = XLSX.utils.aoa_to_sheet(detailedData);
      summarySheet['!cols'] = [{ wpx: 150 }, { wpx: 300 }];
      dataSheet['!cols'] = [{ wpx: 150 }, { wpx: 150 }];
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
      XLSX.utils.book_append_sheet(workbook, dataSheet, 'Data');
      XLSX.writeFile(workbook, `${filename}.xlsx`);

      return true;
    }
  } catch (error) {
    console.error('Error exporting to XLSX:', error);
    return false;
  }
};

/**
 * Export graph data to CSV format
 * Provides detailed raw data with comprehensive column structure
 *
 * @param {Object} data - The data object containing labels and values
 * @param {string} filename - The name for the exported file
 * @param {Object} metadata - Additional metadata (title, subtitle, filters, etc.)
 */
export const exportToCSV = (data, filename, metadata = {}) => {
  try {
    let csvContent = '';

    // Add metadata headers in horizontal format (Line 1: Headers, Line 2: Values)
    const headers = [];
    const values = [];

    if (metadata.title) {
      headers.push('Graph Name');
      values.push(`"${metadata.title}"`);
    }
    if (metadata.selectedDataset) {
      headers.push('Selected Dataset');
      values.push(`"${metadata.selectedDataset}"`);
    }
    if (metadata.selectedGroup) {
      headers.push('Selected Group');
      values.push(`"${metadata.selectedGroup}"`);
    }
    if (metadata.selectedUsers) {
      headers.push('Selected Users');
      values.push(`"${metadata.selectedUsers}"`);
    }
    if (metadata.dateRange) {
      headers.push('Date Range');
      values.push(`"${metadata.dateRange}"`);
    }
    if (metadata.subtitle) {
      headers.push('Description');
      values.push(`"${metadata.subtitle}"`);
    }
    headers.push('Export Date');
    values.push(`"${new Date().toLocaleString()}"`);

    // Add horizontal headers and values
    csvContent += headers.join(',') + '\n';
    csvContent += values.join(',') + '\n';
    csvContent += '\n';

    // Add data
    if (data.labels && data.values) {
      // Add headers
      csvContent += `${metadata.labelHeader || 'Label'},${metadata.valueHeader || 'Value'}\n`;

      // Add data rows
      data.labels.forEach((label, index) => {
        const value = data.values[index] || 0;
        // Escape commas and quotes in labels
        const escapedLabel = `"${String(label).replace(/"/g, '""')}"`;
        csvContent += `${escapedLabel},${value}\n`;
      });

      // Add summary statistics
      csvContent += '\n';
      csvContent += '# Summary Statistics\n';
      csvContent += `Total,${data.values.reduce((sum, val) => sum + val, 0)}\n`;
      csvContent += `Average,${(data.values.reduce((sum, val) => sum + val, 0) / data.values.length).toFixed(2)}\n`;
      csvContent += `Maximum,${Math.max(...data.values)}\n`;
      csvContent += `Minimum,${Math.min(...data.values)}\n`;
    } else if (Array.isArray(data)) {
      // If data is an array of objects, convert to CSV format
      if (data.length > 0) {
        const headers = Object.keys(data[0]);
        csvContent += headers.map(h => `"${h}"`).join(',') + '\n';

        data.forEach(row => {
          const values = headers.map(header => {
            const value = row[header];
            // Escape commas and quotes
            return `"${String(value || '').replace(/"/g, '""')}"`;
          });
          csvContent += values.join(',') + '\n';
        });
      }
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return false;
  }
};

/**
 * Generic export handler that routes to appropriate export function
 *
 * @param {string} format - 'xlsx' or 'csv'
 * @param {Object} data - The data to export
 * @param {string} filename - The name for the exported file
 * @param {Object} metadata - Additional metadata
 * @param {HTMLCanvasElement} chartCanvas - Optional canvas element for chart image
 */
export const handleExport = async (format, data, filename, metadata = {}, chartCanvas = null) => {
  if (format === 'xlsx') {
    return await exportToXLSX(data, filename, metadata, chartCanvas);
  } else if (format === 'csv') {
    return exportToCSV(data, filename, metadata);
  } else {
    console.error('Unsupported export format:', format);
    return false;
  }
};
